import React, { useEffect, useState } from 'react';
import { Col, Modal, Row, Table } from 'reactstrap';
import { Loader } from './Loader';
import moment from 'moment';

/**
 * A plain breakdown of a utilization charge.
 *
 * Deliberately not styled as a receipt: no letterhead, no GST TIN, no "Tax
 * Invoice" heading and nothing to download. Utilization charges are settled
 * from wallet balance that was already taxed at top-up, so the tax invoice is
 * the top-up receipt — presenting this as one would be wrong.
 */
export const UtilizationModal = ({ visible, toggle, getRequest = () => false, parentID = null, size = 'lg' }) => {

    const [loading, setLoading] = useState(false)
    const [data, setData] = useState({})

    useEffect(() => {
        const getData = async () => {
            setLoading(true)
            const response = await getRequest({ id: parentID })
            if (response && response.data) {
                setData(response.data)
            }
            setLoading(false)
        }
        if (visible && parentID) {
            getData();
        } else {
            setData({})
        }
        return () => setData({})
    }, [visible, parentID, getRequest])

    const rows = data?.transaction_details || []

    // The permit and the period it covers are what people open this for, so
    // they are stated at the top rather than read out of the line item.
    const permitLine = rows.find(r => String(r.item_name || '').startsWith('Harbor Permit - '))
    const permitName = permitLine ? String(permitLine.item_name).replace('Harbor Permit - ', '') : null

    const field = (label, value) => value ? (
        <Col md="6" xs="12" style={{ paddingBottom: '6px' }}>
            <div className="text-muted" style={{ fontSize: '0.8em' }}>{label}</div>
            <div>{value}</div>
        </Col>
    ) : null

    return (
        <Modal size={size} isOpen={visible} toggle={() => toggle({})} keyboard={false} backdrop="static">
            <Loader loading={loading} />
            <div className="modal-header">
                <h5 className="modal-title mt-0">Utilization Details</h5>
                <button type="button" onClick={() => toggle({})} className="close" aria-label="Close"><span aria-hidden="true">&times;</span></button>
            </div>
            <div className="modal-body">
                {permitName ? (
                    <div className="mb-3 pb-3 border-bottom">
                        <div className="text-muted" style={{ fontSize: '0.8em' }}>Permit Duration</div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 600, lineHeight: 1.2 }}>{permitName}</div>
                        {permitLine.description ? (
                            <div style={{ fontSize: '1rem' }}>{permitLine.description}</div>
                        ) : null}
                    </div>
                ) : null}

                <Row>
                    {field('Reference', data?.transaction_number)}
                    {field('Date', data?.created_at ? moment(data.created_at).format('DD MMM YYYY, HH:mm') : null)}
                    {field('Vessel', data?.vessel_label)}
                    {field('Type', data?.transaction_type)}
                </Row>

                <Table className="table-sm mt-3 mb-0" responsive>
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th className="text-center">Qty</th>
                            <th className="text-end">Unit</th>
                            <th className="text-end">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length ? rows.map((item, i) => (
                            <tr key={i}>
                                <td>
                                    {item.item_name}
                                    {item.description && item !== permitLine ? (
                                        <div className="text-muted" style={{ fontSize: '0.85em' }}>{item.description}</div>
                                    ) : null}
                                </td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="text-end">{item.unit_price}</td>
                                <td className="text-end">{item.total}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan="4" className="text-center text-muted">No items</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colSpan="3" className="text-end">Total</th>
                            <th className="text-end">{data?.net_amount}</th>
                        </tr>
                    </tfoot>
                </Table>

                <div className="text-muted mt-3" style={{ fontSize: '0.85em' }}>
                    Paid from Top-up balance. GST was charged on the top-up. See
                    top-up receipt for the tax invoice.
                </div>
            </div>
            <div className="modal-footer">
                <button type="button" onClick={() => toggle({})} className="btn btn-secondary waves-effect">Close</button>
            </div>
        </Modal>
    )
}
